'use client';

import Image from 'next/image';

import { GroqImage } from '@/helpers/Image';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { CountUp, RevealBlock } from './reveal';

import texture from '@/public/riz-affiliate/about-stat-texture.png';

type Stat = NonNullable<PageRizAffiliateSchema['aboutStats']>[number];

function StatCard({ stat, index }: { stat: Stat; index: number }) {
  return (
    <RevealBlock
      as="li"
      index={index}
      className="relative overflow-clip grid items-center grid-cols-[5rem_1fr_auto] gap-x-[1.5rem] gap-y-[0.25rem] border border-[#2F2F2F]/10 bg-[#F2F4F3]/70 p-[2rem] lg:grid-cols-[5rem_1fr] lg:gap-y-[1.5rem] lg:p-[3rem]">
      <span className="flex items-center justify-center row-span-2 w-[5rem] h-[5rem] rounded-[0.5rem] border border-[#296E5A]/10 bg-[#296E5A]/5 lg:row-span-1">
        {stat?.icon && (
          <GroqImage
            src={stat.icon}
            alt=""
            quality={100}
            sizes="20px"
            style={{ width: '2.5rem', height: '2.5rem' }}
            className="object-contain"
          />
        )}
      </span>
      {stat?.label && (
        <p className="m-0 self-end font-geist font-[500] text-black/[0.64] text-[1.75rem] leading-[2.5rem] lg:self-center">
          {stat.label}
        </p>
      )}
      {stat?.value && (
        <p className="m-0 row-span-2 col-start-3 row-start-1 whitespace-nowrap font-geist font-[500] text-[#001B14] text-[4rem] leading-[5rem] tracking-[-0.04em] lg:col-span-2 lg:col-start-1 lg:row-span-1 lg:row-start-2 lg:text-[5rem] lg:leading-[5.75rem]">
          <CountUp value={stat.value} />
        </p>
      )}
      {stat?.caption && (
        <p className="m-0 col-start-2 row-start-2 self-start font-geist font-[400] text-[#001B14]/60 text-[1.75rem] leading-[2.5rem] lg:col-span-2 lg:col-start-1 lg:row-start-3 lg:text-[2rem] lg:leading-[3rem]">
          {stat.caption}
        </p>
      )}
      <Image
        src={texture}
        alt=""
        aria-hidden
        sizes="(min-width: 993px) 36vw, 160vw"
        className="pointer-events-none absolute top-0 max-w-none opacity-20 mix-blend-exclusion left-[27.625rem] w-[85.875rem] h-auto"
      />
    </RevealBlock>
  );
}

export default function AboutStats({ data }: { data: PageRizAffiliateSchema }) {
  if (!data?.aboutStats?.length) return null;
  return (
    <ul className="m-0 grid list-none grid-cols-1 gap-[1rem] p-0 lg:grid-cols-3 lg:gap-[2rem]">
      {data.aboutStats.map((stat, i) => (
        <StatCard
          key={`${stat?.label ?? 'stat'}-${i}`}
          stat={stat}
          index={i}
        />
      ))}
    </ul>
  );
}
